import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReportCard } from '@components/Pages/AnalyticsPage/ReportCard';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import { DateTimeField } from '@components/ui/DateTimeField/DateTimeField';
import { useSiteTags } from '../hooks/useSiteTags';
import { useSiteEquipment } from '../hooks/useSiteEquipment';
import type { AnalyticsTag, AnalyticsEquipment } from '@apis/analytics';
import type { ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, nowInProjectAsLocal } from '@utils/dateUtils';
import { CardIcon } from '../CardIcon';
import { rollingMonthsAgo, todayCap } from '../utils/dateRules';

export type IEntityKind = 'tag' | 'equipment';

/**
 * What the card emits via `onSubmit`. Same shape family as DailyRoute's —
 * the page handler converts `at` to epoch ms when building the
 * create-report payload, and forwards `entityName` + `entityNick` so the
 * archived record carries them for the recent-reports rail.
 */
export interface IIncidentReportSubmission {
  entityKind: IEntityKind;
  entityId: string;
  entityName: string;
  entityNick: string;
  at: Date;
}

interface IIncidentReportCardProps {
  isSelected: boolean;
  onSelect: (name: 'incidentReport') => void;
  onSubmit: (payload: IIncidentReportSubmission) => void;
}

/** `${tag.name}-${tag.tagNick}` — falls back to nickname when name is empty. */
function tagToOption(tag: AnalyticsTag): ISelectionOption {
  const label = tag.name ? `${tag.name}-${tag.tagNick}` : tag.tagNick;
  return { id: tag.tagId, label };
}

/** `${equipment.typeNick}-${equipment.name}` — falls back to typeNick. */
function equipmentToOption(equipment: AnalyticsEquipment): ISelectionOption {
  const label = equipment.name
    ? `${equipment.typeNick}-${equipment.name}`
    : equipment.typeNick;
  return { id: equipment.equipmentId, label };
}

export function IncidentReportCard({ isSelected, onSelect, onSubmit }: IIncidentReportCardProps) {
  const { t } = useTranslation();
  const { siteId = '' } = useParams<{ siteId: string }>();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const [entityKind, setEntityKind] = useState<IEntityKind>('tag');
  const [entityId, setEntityId] = useState<string | null>(null);
  const [at, setAt] = useState<Date | null>(null);

  // Hooks run unconditionally; the queries gate themselves on siteId.
  const tagsQuery = useSiteTags(siteId);
  const equipmentQuery = useSiteEquipment(siteId);

  const tagOptions: ISelectionOption[] = useMemo(
    () => (tagsQuery.data ?? []).map(tagToOption),
    [tagsQuery.data],
  );
  const equipmentOptions: ISelectionOption[] = useMemo(
    () => (equipmentQuery.data ?? []).map(equipmentToOption),
    [equipmentQuery.data],
  );

  const options = entityKind === 'tag' ? tagOptions : equipmentOptions;
  const canSubmit = entityId !== null && at !== null;

  // Auto-fill the datetime to "now in project tz" when the user picks an
  // entity. Matches the IncidentReport UX in trusstor: the common case is
  // investigating an event that just happened — picking the tag and having
  // to also pick today's calendar day + a slot near current time is
  // friction. The user can still override either field afterwards.
  const handleEntityChange = (id: string | null) => {
    setEntityId(id);
    if (id !== null) {
      setAt(nowInProjectAsLocal(tz));
    }
  };

  return (
    <ReportCard
      name="incidentReport"
      title={t('analytics.cards.incidentReport.title')}
      description={t('analytics.cards.incidentReport.description')}
      icon={<CardIcon name="calendar-plus" />}
      isSelected={isSelected}
      onSelect={() => onSelect('incidentReport')}
      onSubmit={() => {
        if (!entityId || !at) return;
        // Pluck name + nick from the cached query data so the archived
        // record can carry entity context (and the recent-reports rail
        // doesn't need to re-fetch the tag/equipment catalog).
        const tag =
          entityKind === 'tag'
            ? tagsQuery.data?.find((x) => x.tagId === entityId)
            : undefined;
        const eq =
          entityKind === 'equipment'
            ? equipmentQuery.data?.find((x) => x.equipmentId === entityId)
            : undefined;
        // `||` (not `??`): the BFF returns `''` for missing upstream names,
        // and we want that to fall through to the next option rather than
        // surface as an empty heading downstream.
        const entityName = tag?.name || eq?.name || entityId;
        const entityNick = tag?.tagNick || eq?.typeNick || entityId;
        onSubmit({ entityKind, entityId, entityName, entityNick, at });
      }}
      submitLabel={t('analytics.createButton')}
      submitDisabled={!canSubmit}
    >
      <SegmentedControl
        value={entityKind}
        onChange={(v) => {
          setEntityKind(v);
          setEntityId(null);
        }}
        options={[
          { id: 'tag', label: t('analytics.filters.switchToTag') },
          { id: 'equipment', label: t('analytics.filters.switchToEquipment') },
        ]}
        ariaLabel={t('analytics.filters.tag')}
      />
      <SingleSelect
        value={entityId}
        onChange={handleEntityChange}
        options={options}
        placeholder={t('analytics.filters.selectPlaceholder')}
        ariaLabel={entityKind === 'tag' ? t('analytics.filters.tag') : t('analytics.filters.equipment')}
      />
      <DateTimeField
        value={at}
        onChange={setAt}
        placeholder={t('analytics.filters.selectPlaceholder')}
        ariaLabel={t('analytics.filters.dateTime')}
        minDate={rollingMonthsAgo(4, tz)}
        maxDate={todayCap(tz)}
        disabled={!entityId}
      />
    </ReportCard>
  );
}
